import React, { useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import API from '../../../../services/API'
import Header from './Header'

import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import IncidentModule from './IncidentModule'
import DemographicsModule from './DemographicsModule'
import HxModule from './HxModule'
import AssessModule from './AssessModule'
import VitalsModule from './VitalsModule'
import TreatmentModule from './TreatmentModule'
import LiveTimelineModule from './LiveTimelineModule'
import ClinicalAdviceLine from './ClinicalAdviceLine'
import AccessLogs from './AccessLogs'
import { Alert, Box, Modal, Skeleton } from '@mui/material'
import BigModal from '../../../../components/Modals/BigModal'
import EpcrPDF from '../../Sharing/PDF/PatientRecordPDF'
import { TenantSettingsContext, UserContext } from '../../../../App'
import { PDFViewer } from '@react-pdf/renderer'
import ShareCode from '../../ePCR/Sharing/ShareCode'
import AccessPopup from './AccessPopup'
import HSIncidentModule from './HSIncidentModule'
import Plan from './Plan'
import Safeguarding from './Safeguarding'
import Attachments from './Attachments'
import WelfareModule from './WelfareModule'
import ImagingRequestsPanel from '../../ePCR/Imaging/ImagingRequestsPanel'
import SubscriptionGate from '../../../../components/Subscriptions/SubscriptionGate'

const EPCRView = () => {
  const { id } = useParams()
  const [loading, setLoading] = useState(true)
  const [record, setRecord] = useState({})
  const [openPDF, setOpenPDF] = useState(false)
  const [accessLogged, setAccessLogged] = useState(false)
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig
  const { user } = useContext(UserContext)
  const [calNotes, setCalNotes] = useState([])

  const navigate = useNavigate()
  const loadRecord = () => {
    API.get(`/epcrreport/${id}/${accessLogged}`).then((res) => {
      if (res.status === 200) {
        setRecord(res.data)
        setLoading(false)
      } else if (res.status === 403) {
        navigate('/patient-report-forms')
      }
    })
  }
  useEffect(() => {
    if (accessLogged) {
      loadRecord()
    }
  }, [accessLogged])

  const handlePDFDownload = ({ anonymised }) => {
    setOpenPDF({ anonymised })
  }

  return (
    <>
      {!accessLogged ? (
        <Skeleton variant="rounded" width="100%" height={200}>
          <AccessPopup epcrID={id} setAccessLogged={setAccessLogged} />
        </Skeleton>
      ) : loading ? (
        <LinearProgress />
      ) : Object.keys(record).length > 0 ? (
        <Stack gap={2}>
          <Header
            record={record}
            loadRecord={loadRecord}
            handlePDFDownload={handlePDFDownload}
          />
          <Stack paddingX={1} alignItems="center">
            <Typography variant="caption" color="#c00000">
              The information contained herein is privileged and confidential
              and is protected by UK Data Protection laws, including the Data
              Protection Act 2018, and the Computer Misuse Act 1990.
            </Typography>
            <Typography variant="caption" color="#c00000">
              Any unauthorised access, disclosure, distribution, or use of this
              software is strictly prohibited.
            </Typography>
          </Stack>
          <DemographicsModule demographics={record.demographics} />
          <IncidentModule incident={record.incident} />
          {record?.mode === 'Welfare' && (
            <WelfareModule welfare={record.welfare} />
          )}
          {record?.mode !== 'Welfare' && (
            <Safeguarding
              safeguarding={record.safeguarding}
              setRecord={setRecord}
              epcrID={record._id}
            />
          )}
          {record?.incident?.healthSafetyConcern && (
            <HSIncidentModule
              incident={record.incident}
              epcrID={record._id}
              setRecord={setRecord}
              ePCRStatus={record?.review?.status}
            />
          )}
          {record?.mode !== 'Welfare' && (
            <>
              <HxModule pmhx={record.pmhx} hx={record.history} />
              <AssessModule assess={record.assess} />
              <VitalsModule
                vitals={record.vitals?.obs}
                spo2Scale2={record.pmhx.spo2Scale2}
                demographics={record.demographics}
              />
              <TreatmentModule treatment={record.treatment} />
              <LiveTimelineModule record={record} />
              <Plan plan={record.plan} />
            </>
          )}
          <Attachments attachments={record?.attachments ?? []} />
          {record?.mode !== 'Welfare' && (
            <>
              <ImagingRequestsPanel
                epcrID={record._id}
                title="Imaging"
                hideWhenEmpty
              />
              <ClinicalAdviceLine
                originalNotes={record?.clinicalAdviceLineNotes ?? []}
                epcrID={record._id}
                calNotes={calNotes}
                setCALNotes={setCalNotes}
                currentTreatmentLocation={
                  record?.review?.currentTreatmentLocation
                }
              />
            </>
          )}
          <ShareCode
            ePCRID={record._id}
            passedCodes={record.review.shareCodes}
            currentTreatmentLocation={record?.review?.currentTreatmentLocation}
          />

          <BigModal open={!!openPDF} onClose={() => setOpenPDF(false)}>
            <Box
              sx={{
                minHeight: '80vh',

                display: 'flex',

                alignItems: 'center',

                justifyContent: 'center',

                p: 2,
              }}
            >
              <SubscriptionGate
                requiredTier={2}
                featureName="ePCR Downloads"
                onGoBack={() => setOpenPDF(false)}
                areNotIs
                hideChildrenWhenBlocked
                caption="Generate professional PDF patient care records for sharing, governance, and clinical continuity."
                benefits={[
                  'Download professional PDF patient care records instantly.',
                  'Share records securely with hospitals and healthcare providers.',
                  'Maintain clear documentation for governance and auditing.',
                  'Create printable records for operational and legal use.',
                ]}
              >
                <Box sx={{ height: '80vh', width: '100%' }}>
                  <PDFViewer width="100%" height="100%">
                    <EpcrPDF
                      epcr={record}
                      calNotes={calNotes}
                      anonymised={openPDF?.anonymised}
                      orgSettings={orgSettings}
                      onRender={() => {
                        API.post('/epcrreport/accesslog', {
                          epcrID: id,
                          note: 'PDF Downloaded',
                        })
                      }}
                      printedBy={`${user.firstName} ${user.lastName} (${user.employee?.clinicalRole ?? user.subcontractor?.clinicalRole ?? 'Clinician'})`}
                    />
                  </PDFViewer>
                </Box>
              </SubscriptionGate>
            </Box>
          </BigModal>
        </Stack>
      ) : (
        <Alert severity="error">Unable to load record</Alert>
      )}
    </>
  )
}

export default EPCRView
